import type { BotDifficulty, CardDeckId } from "@bing/shared";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { DeckPicker } from "./DeckPicker";
import { DifficultySelect } from "./DifficultySelect";
import { StartRow, StartScreenShell } from "./StartScreenShell";

/**
 * The pre-deal screen every SOLO CARD game opens on — حکم, بی‌دل, پاسور, بلوف and شلم.
 *
 * It asks the two questions that used to live in Settings and that (per the operator's report)
 * players never found there: how strong the bots are, and which card design to play with. Both
 * are still the SHARED preferences the connectors already read (`bing:botDifficulty` /
 * `bing:deck`) — this screen preselects the remembered answer and writes the pick back, so a
 * player who always wants «سخت» answers once and every game re-offers it.
 *
 * The shell — felt, header, centred card, «شروع بازی», and the sizing budget that keeps all of it
 * on one screen at 320×568 — is {@link StartScreenShell}, shared with شطرنج/تخته نرد/منچ. A game's
 * OWN rules (حکم's match length and its سرس/نرس switches, شلم's match target) come in as `children`
 * ABOVE these two rows: the rules of the match first, then the opponent and the look.
 */
export function SoloStartScreen({
  title,
  emoji,
  heading,
  leaveLabel,
  difficulty,
  onDifficultyChange,
  deck,
  onDeckChange,
  onStart,
  onLeave,
  children,
}: {
  title: string;
  emoji: string;
  heading: string;
  leaveLabel: string;
  difficulty: BotDifficulty;
  onDifficultyChange: (value: BotDifficulty) => void;
  deck: CardDeckId;
  onDeckChange: (value: CardDeckId) => void;
  onStart: () => void;
  onLeave: () => void;
  /** The game's OWN pre-deal controls, rendered above the difficulty + deck rows. */
  children?: ReactNode;
}): ReactNode {
  return (
    <StartScreenShell
      title={title}
      emoji={emoji}
      heading={heading}
      leaveLabel={leaveLabel}
      onStart={onStart}
      onLeave={onLeave}
    >
      {children}
      <StartRow label={fa.difficulty.labelMain}>
        <DifficultySelect
          value={difficulty}
          onChange={onDifficultyChange}
          ariaLabel={fa.difficulty.labelMain}
          compact
        />
      </StartRow>
      {/* The deck picker names itself (`fa.online.cardDeck`) for AT; the visible label above it is
          the same words, so the two can never drift. */}
      <StartRow label={fa.online.cardDeck}>
        <DeckPicker value={deck} onChange={onDeckChange} compact />
      </StartRow>
    </StartScreenShell>
  );
}
